if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {};
        this.initialized = false;
        this.mql = window.matchMedia('(min-width: 750px)');

        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.onSlideChanged = debounce(
          this.onSlideChanged.bind(this),
          500
        );

        /*
         * Shopify can replace the gallery's internal HTML without replacing
         * the <media-gallery> element itself. Reconnect listeners if that
         * happens.
         */
        this.galleryObserver = new MutationObserver(() => {
          const viewer = this.querySelector('[id^="GalleryViewer"]');
          const thumbnails = this.querySelector(
            '[id^="GalleryThumbnails"]'
          );

          if (
            viewer !== this.elements.viewer ||
            thumbnails !== this.elements.thumbnails
          ) {
            this.removeControlListeners();
            this.initialized = false;
            this.initialize();
          }
        });
      }

      connectedCallback() {
        this.galleryObserver.observe(this, {
          childList: true,
          subtree: true,
        });

        this.initialize();
      }

      disconnectedCallback() {
        this.galleryObserver.disconnect();
        this.removeControlListeners();
        this.initialized = false;
      }

      initialize() {
        if (this.initialized) return;

        const viewer = this.querySelector('[id^="GalleryViewer"]');

        /*
         * When the custom element is created before its children are parsed,
         * the MutationObserver will call initialize() again once they exist.
         */
        if (!viewer) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer,
          thumbnails: this.querySelector(
            '[id^="GalleryThumbnails"]'
          ),
        };

        this.elements.viewer.addEventListener(
          'slideChanged',
          this.onSlideChanged
        );

        /*
         * Delegate thumbnail clicks so thumbnails inserted during a variant
         * update work without attaching new listeners to every button.
         */
        this.elements.thumbnails?.addEventListener(
          'click',
          this.onThumbnailClick
        );

        this.mql.addEventListener?.('change', this.onViewportChange);
        this.initialized = true;

        if (
          this.dataset.desktopLayout?.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      removeControlListeners() {
        this.elements.viewer?.removeEventListener(
          'slideChanged',
          this.onSlideChanged
        );
        this.elements.thumbnails?.removeEventListener(
          'click',
          this.onThumbnailClick
        );
        this.mql.removeEventListener?.('change', this.onViewportChange);
      }

      onViewportChange(event) {
        if (
          event.matches &&
          this.dataset.desktopLayout?.includes('thumbnail')
        ) {
          this.removeListSemantic();
        }
      }

      onThumbnailClick(event) {
        if (!(event.target instanceof Element)) return;

        const thumbnail = event.target.closest('[data-target]');

        if (
          !thumbnail ||
          !this.elements.thumbnails?.contains(thumbnail)
        ) {
          return;
        }

        this.setActiveMedia(thumbnail.dataset.target, false);
      }

      onSlideChanged(event) {
        const mediaId =
          event.detail?.currentElement?.dataset?.mediaId;

        if (!mediaId || !this.elements.thumbnails) return;

        const thumbnail = Array.from(
          this.elements.thumbnails.querySelectorAll('[data-target]')
        ).find((item) => item.dataset.target === mediaId);

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend = false) {
        if (!mediaId || !this.elements.viewer) return;

        const allMedia = Array.from(
          this.elements.viewer.querySelectorAll('[data-media-id]')
        );
        const activeMedia = allMedia.find(
          (item) => item.dataset.mediaId === String(mediaId)
        );

        if (!activeMedia) {
          console.warn(`MediaGallery: media "${mediaId}" was not found.`);
          return;
        }

        allMedia.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        const activeThumbnail = this.elements.thumbnails
          ? Array.from(
              this.elements.thumbnails.querySelectorAll('[data-target]')
            ).find(
              (item) => item.dataset.target === String(mediaId)
            )
          : null;

        if (prepend) {
          activeMedia.parentElement?.prepend(activeMedia);
          activeThumbnail?.parentElement?.prepend(activeThumbnail);

          if (
            typeof this.elements.viewer.resetPages === 'function'
          ) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          const mediaScroller = activeMedia.parentElement;

          if (
            this.elements.thumbnails &&
            typeof mediaScroller?.scrollTo === 'function'
          ) {
            mediaScroller.scrollTo({ left: activeMedia.offsetLeft });
          }

          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout === 'stacked'
          ) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        }, 0);

        this.playActiveMedia(activeMedia);

        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((button) =>
            button.removeAttribute('aria-current')
          );

        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', 'true');

        /*
         * isSlideVisible() is supplied by Shopify's slider component. Guard
         * it because media-gallery may initialize before slider-component.
         */
        if (
          typeof this.elements.thumbnails.isSlideVisible ===
            'function' &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        const thumbnailScroller =
          this.elements.thumbnails.slider ||
          this.elements.thumbnails.querySelector(
            '.thumbnail-list, .slider'
          ) ||
          this.elements.thumbnails;

        thumbnailScroller.scrollTo?.({
          left: thumbnail.offsetLeft,
          behavior: 'smooth',
        });
      }

      announceLiveRegion(activeItem, position) {
        const liveRegion = this.elements.liveRegion;
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );
        const messageTemplate =
          window.accessibilityStrings?.imageAvailable;

        if (!liveRegion || !image || !messageTemplate || !position) {
          return;
        }

        const announce = () => {
          liveRegion.setAttribute('aria-hidden', 'false');
          liveRegion.innerHTML = messageTemplate.replace(
            '[index]',
            position
          );

          window.setTimeout(() => {
            liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (
          deferredMedia &&
          typeof deferredMedia.loadContent === 'function'
        ) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        this.stickyHeader?.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        const slider = this.elements.viewer?.slider;
        const sliderItems = this.elements.viewer?.sliderItems;

        if (!slider || !sliderItems) return;

        slider.setAttribute('role', 'presentation');
        sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );

  window.fixedMediaGalleryInstalled = true;
}
